<script setup lang="ts">
import { computed, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import ExamDateAsk from '@/components/MountainSuccess/ExamDateAsk.vue'
import { isSupportWindow } from '@/utils'
import { screenModule } from '@/store/screen/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { mobileModule } from '@/store/mobile/module'
import { analyticsModule } from '@/store/analytics/module'

const {
    Button: PocketButton,
    Icon,
} = UIKit

const props = withDefaults(defineProps<{
    isLoading?: boolean
    errorMessage?: string
}>(), {
    isLoading: false,
    errorMessage: '',
})

const emit = defineEmits<{
    'close': []
    'continue': []
}>()

const isShowingExamDateAsk = ref(false)

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const isOOAP = computed(() => {
    return mobileModule.getters.isInArrivedFromMobileState()
})

const scheduledExamDate = computed(() => {
    return userExamMetadataModule.getters.getScheduledExamDate()
})

const returnToMobile = () => {
    if (props.isLoading) {
        return
    }

    mobileModule.actions.returnToMobileApp()

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        callToAction: 'Back to App',
        screenName: 'Premium Success',
    })

    emit('close')
}

const closeClicked = () => {
    if (props.isLoading) {
        return
    }

    emit('close')
}

const continueClicked = () => {
    if (props.isLoading) {
        return
    }

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        callToAction: 'Continue',
        screenName: 'Premium Success',
    })

    emit('continue')

    isShowingExamDateAsk.value = true
}

const contactSupportClicked = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}
</script>

<template>
    <div class="mountain-success">
        <template v-if="!isShowingExamDateAsk">
            <Icon
                class="mountain-success__close-icon"
                :class="{
                    'mountain-success__close-icon--error': errorMessage,
                }"
                type="close"
                @click="closeClicked"
            />
            <div class="mountain-success__title">
                <slot name="successTitle">
                    <div>Welcome to Pocket Prep!</div>
                    <div>You're Premium.</div>
                </slot>
            </div>
            <div class="mountain-success__message">
                <slot name="successMessage">
                    <div class="mountain-success__message-paragraph">
                        You're in good company.
                        91,695 learners are using Pocket Prep Premium to pass their exams—just like you.
                    </div>
                    <div
                        v-if="!errorMessage"
                        class="mountain-success__message-paragraph mountain-success__message-paragraph--delay"
                    >
                        A great place to start is taking a
                        <span class="mountain-success__quick-10">Quick 10 quiz</span>.
                    </div>
                </slot>
            </div>
            <div v-if="errorMessage" class="mountain-success__error-message">
                {{ errorMessage }}
            </div>
            <PocketButton
                v-if="errorMessage"
                class="mountain-success__contact-support-btn"
                :is-loading="isLoading"
                @click="contactSupportClicked"
            >
                Contact Support
            </PocketButton>
            <template v-else>
                <template v-if="scheduledExamDate && isOOAP">
                    <PocketButton
                        class="mountain-success__back-to-app-btn"
                        :is-loading="isLoading"
                        @click="returnToMobile"
                    >
                        Back to App
                    </PocketButton>
                </template>
                <template v-else-if="scheduledExamDate">
                    <PocketButton
                        class="mountain-success__close-btn"
                        :is-loading="isLoading"
                        @click="closeClicked"
                    >
                        Close
                    </PocketButton>
                </template>
                <PocketButton
                    v-else
                    class="mountain-success__continue-btn"
                    :is-loading="isLoading"
                    @click="continueClicked"
                >
                    Continue
                </PocketButton>
            </template>
            <img
                v-if="breakpoint !== 'black-bear'"
                class="mountain-success__image"
                src="@/assets/onboarding/mountain-premium.png"
            >
            <img
                v-else
                class="mountain-success__image-mobile"
                src="@/assets/onboarding/mountain-premium-mobile.png"
            >
        </template>
        <ExamDateAsk
            v-else-if="isShowingExamDateAsk"
            @close="closeClicked"
        />
    </div>
</template>

<style lang="scss" scoped>
.mountain-success {
    background-color: $floral-white;
    display: flex;
    flex-direction: column;
    align-items: center;

    &__close-icon {
        position: absolute;
        right: 14px;
        top: 14px;
        z-index: 10;
        color: $brand-blue;
        width: 30px;
        height: 30px;
        cursor: pointer;
        user-select: none;
        outline: none;
        opacity: 0;
        animation-name: fade-in;
        animation-duration: 0.7s;
        animation-delay: 2.5s;
        animation-fill-mode: forwards;

        &:not(&--error) {
            @include breakpoint(black-bear) {
                display: none;
            }
        }

        &:hover {
            color: $brand-black;
        }

        &:focus::before {
            content: '';
            left: -1px;
            top: -1px;
            width: 100%;
            height: 100%;
            position: absolute;
            border: 1px solid $brand-blue;
            border-radius: 5px;
        }

        &--dark {
            color: $banana-bread;

            &:hover {
                color: $butterscotch;
            }

            &:focus::before {
                border-color: $banana-bread;
            }
        }

        svg {
            width: 100%;
            height: 100%;
        }
    }

    &__title {
        font-size: 26px;
        outline: none;
        line-height: 36px;
        font-weight: 700;
        text-align: center;
        margin: 0;
        padding: 0 25px;
        z-index: 1;
        opacity: 0;
        animation-name: fade-in;
        animation-duration: 1s;
        animation-fill-mode: forwards;

        @include breakpoint(black-bear) {
            max-width: 321px;
        }
    }

    &__message {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 22px;
        width: 456px;
        font-size: 15px;
        line-height: 20px;
        text-align: center;
        z-index: 1;
        opacity: 0;
        animation-name: fade-in;
        animation-duration: 1.5s;
        animation-delay: 1s;
        animation-fill-mode: forwards;

        @include breakpoint(black-bear) {
            max-width: 321px;
        }
    }

    &__message-paragraph {
        margin-top: 12px;

        &--delay {
            opacity: 0;
            animation-name: fade-in;
            animation-duration: 1.5s;
            animation-delay: 2.5s;
            animation-fill-mode: forwards;
        }
    }

    &__quick-10 {
        font-weight: 600;
    }

    &__error-message {
        margin-top: -10px;
        margin-bottom: 22px;
        width: 456px;
        font-size: 15px;
        line-height: 20px;
        text-align: center;
        z-index: 1;
        color: $pepper;
        opacity: 0;
        animation-name: fade-in;
        animation-duration: 1.5s;
        animation-delay: 2.5s;
        animation-fill-mode: forwards;

        @include breakpoint(black-bear) {
            max-width: 321px;
        }
    }

    &__contact-support-btn,
    &__close-btn,
    &__continue-btn,
    &__back-to-app-btn {
        z-index: 1;
        opacity: 0;
        animation-name: fade-in;
        animation-duration: 0.7s;
        animation-delay: 2.5s;
        animation-fill-mode: forwards;

        @include breakpoint(black-bear) {
            margin-top: 14px;
        }
    }

    &__image {
        position: absolute;
        top: 177px;
        max-width: 750px;
        max-height: 660px;
        z-index: 0;
    }

    &__image-mobile {
        width: 100%;
        position: absolute;
        top: 230px;
        z-index: 0;
    }

    @keyframes fade-in {
        0% {
            opacity: 0;
        }

        100% {
            opacity: 1;
        }
    }
}
</style>
